/*
 * WSH FYS-P3 — Start-page right-side turning panel.
 *
 * Shipped 2026-10-16. Scope: /pages/find-your-sound Start view only.
 *
 * Behaviour (per brief §3.5):
 *   - Auto-advance every 6s, 1 → 2 → 3 → 1 (loops). The current tab's
 *     progress line fills over those 6s via rAF-driven scaleX.
 *   - Pause while pointer is over the panel or tabs, while any element
 *     inside has keyboard focus, and while document.hidden is true.
 *   - Tap or click a tab (or Next) jumps to that step AND stops
 *     auto-advance for the rest of the visit.
 *   - prefers-reduced-motion: reduce → no auto-advance, no fill, no
 *     fade; steps change only on tap. Reactive to media-query changes.
 *   - Keyboard: real ARIA tablist. Left/Right arrows move between
 *     tabs; Home/End go to ends; Enter/Space selects. Auto-advance
 *     must not move keyboard focus (verified: timer path never calls
 *     .focus()).
 *   - Phones: horizontal swipe on the panel body moves one step.
 *     > 40px horizontal AND more horizontal than vertical. Passive
 *     listeners so vertical scrolling is never blocked.
 *   - Idempotent across section re-renders via `dataset.wshBound`.
 */
(function () {
  'use strict';

  var PANEL_SELECTOR = '[data-wsh-intro-panel]';
  var AUTO_MS = 6000;
  var SWIPE_MIN = 40;

  function initPanel(panel) {
    if (!panel || panel.dataset.wshBound === '1') return;
    panel.dataset.wshBound = '1';

    var tabs = Array.prototype.slice.call(panel.querySelectorAll('[data-wsh-intro-tab]'));
    var steps = Array.prototype.slice.call(panel.querySelectorAll('[data-wsh-intro-step]'));
    var fills = {};
    tabs.forEach(function (t) {
      var i = t.getAttribute('data-wsh-intro-tab');
      var f = panel.querySelector('[data-wsh-intro-fill="' + i + '"]');
      if (f) fills[i] = f;
    });
    var nextName = panel.querySelector('[data-wsh-intro-next-name]');
    var stepIds = tabs.map(function (t) { return t.getAttribute('data-wsh-intro-tab'); });
    if (stepIds.length < 2) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var state = {
      current: stepIds[0],
      userStopped: false,
      hovering: false,
      focused: false,
      hidden: !!document.hidden,
      startTime: 0,
      elapsed: 0,
      rafId: 0,
    };

    function clearRaf() {
      if (state.rafId) {
        cancelAnimationFrame(state.rafId);
        state.rafId = 0;
      }
    }

    function activeFill() {
      return fills[state.current] || null;
    }

    function setActive(id, opts) {
      opts = opts || {};
      var fromUser = !!opts.fromUser;
      state.current = id;
      state.elapsed = 0;
      var activeIdx = stepIds.indexOf(id);
      tabs.forEach(function (t) {
        var i = t.getAttribute('data-wsh-intro-tab');
        var tIdx = stepIds.indexOf(i);
        var active = i === id;
        var past = tIdx < activeIdx;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
        var f = fills[i];
        if (f) f.style.transform = past ? 'scaleX(1)' : 'scaleX(0)';
      });
      steps.forEach(function (s) {
        var i = s.getAttribute('data-wsh-intro-step');
        s.classList.toggle('is-active', i === id);
      });
      if (nextName) {
        var nextIdx = (activeIdx + 1) % stepIds.length;
        var nextTab = tabs[nextIdx];
        if (nextTab) nextName.textContent = nextTab.getAttribute('data-wsh-intro-name') || '';
      }
      if (fromUser) {
        state.userStopped = true;
        clearRaf();
      } else {
        schedule();
      }
    }

    function schedule() {
      clearRaf();
      if (state.userStopped) return;
      if (reduceMotion.matches) return;
      if (state.hovering || state.focused || state.hidden) return;
      state.startTime = performance.now() - state.elapsed;
      state.rafId = requestAnimationFrame(tick);
    }

    function tick(now) {
      if (state.userStopped) return;
      if (state.hovering || state.focused || state.hidden) {
        state.elapsed = now - state.startTime;
        return;
      }
      var dt = now - state.startTime;
      state.elapsed = dt;
      var pct = Math.min(1, dt / AUTO_MS);
      var f = activeFill();
      if (f) f.style.transform = 'scaleX(' + pct + ')';
      if (dt >= AUTO_MS) {
        var idx = stepIds.indexOf(state.current);
        var nextId = stepIds[(idx + 1) % stepIds.length];
        setActive(nextId);
        return;
      }
      state.rafId = requestAnimationFrame(tick);
    }

    function pauseNow() {
      clearRaf();
    }

    // --- Clicks: tabs + Next ---
    panel.addEventListener('click', function (e) {
      var tabEl = e.target.closest && e.target.closest('[data-wsh-intro-tab]');
      if (tabEl && panel.contains(tabEl)) {
        var id = tabEl.getAttribute('data-wsh-intro-tab');
        if (id !== state.current) setActive(id, { fromUser: true });
        else { state.userStopped = true; clearRaf(); }
        return;
      }
      var nextEl = e.target.closest && e.target.closest('[data-wsh-intro-next]');
      if (nextEl && panel.contains(nextEl)) {
        var idx = stepIds.indexOf(state.current);
        var nextId = stepIds[(idx + 1) % stepIds.length];
        setActive(nextId, { fromUser: true });
      }
    });

    // --- Keyboard: ARIA tablist ---
    var tablist = panel.querySelector('[role="tablist"]');
    if (tablist) {
      tablist.addEventListener('keydown', function (e) {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', ' '].indexOf(e.key) < 0) return;
        var activeTab = tablist.querySelector('[role="tab"][aria-selected="true"]') || tabs[0];
        var idx = tabs.indexOf(activeTab);
        var newIdx = idx;
        if (e.key === 'ArrowLeft') newIdx = (idx - 1 + tabs.length) % tabs.length;
        else if (e.key === 'ArrowRight') newIdx = (idx + 1) % tabs.length;
        else if (e.key === 'Home') newIdx = 0;
        else if (e.key === 'End') newIdx = tabs.length - 1;
        else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          var id = activeTab.getAttribute('data-wsh-intro-tab');
          setActive(id, { fromUser: true });
          activeTab.focus();
          return;
        }
        if (newIdx !== idx) {
          e.preventDefault();
          var t = tabs[newIdx];
          var newId = t.getAttribute('data-wsh-intro-tab');
          setActive(newId, { fromUser: true });
          t.focus();
        }
      });
    }

    // --- Pause rules: hover, focus-within, document hidden ---
    panel.addEventListener('mouseenter', function () { state.hovering = true; pauseNow(); });
    panel.addEventListener('mouseleave', function () { state.hovering = false; schedule(); });
    panel.addEventListener('focusin', function () { state.focused = true; pauseNow(); });
    panel.addEventListener('focusout', function (e) {
      if (!panel.contains(e.relatedTarget)) {
        state.focused = false;
        schedule();
      }
    });
    document.addEventListener('visibilitychange', function () {
      state.hidden = !!document.hidden;
      if (state.hidden) pauseNow();
      else schedule();
    });
    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', function () {
        if (reduceMotion.matches) clearRaf();
        else schedule();
      });
    }

    // --- Swipe (phones): on the panel body ---
    var body = panel.querySelector('[data-wsh-intro-body]');
    if (body) {
      var sx = 0, sy = 0, dx = 0, dy = 0;
      body.addEventListener('touchstart', function (e) {
        var t = e.touches && e.touches[0];
        if (!t) return;
        sx = t.clientX; sy = t.clientY; dx = 0; dy = 0;
      }, { passive: true });
      body.addEventListener('touchmove', function (e) {
        var t = e.touches && e.touches[0];
        if (!t) return;
        dx = t.clientX - sx; dy = t.clientY - sy;
      }, { passive: true });
      body.addEventListener('touchend', function () {
        if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
          var idx = stepIds.indexOf(state.current);
          var dir = dx < 0 ? 1 : -1;
          var newIdx = (idx + dir + stepIds.length) % stepIds.length;
          setActive(stepIds[newIdx], { fromUser: true });
        }
        sx = 0; sy = 0; dx = 0; dy = 0;
      }, { passive: true });
    }

    // Kick off: paint tabs, start timer unless paused by rules above.
    setActive(state.current);
  }

  function init() {
    document.querySelectorAll(PANEL_SELECTOR).forEach(initPanel);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
